import type { PerfilUsuario } from '@/features/auth/tipos';

/**
 * De onde veio a solicitação.
 *
 * Espelho de `backend/src/solicitacoes/origem.ts`. A **demanda reprimida** é o
 * estoque de pedidos que chegou à secretaria antes da implantação do KAVI: o
 * fluxo é o mesmo da solicitação da UBS, e o que muda é quem registra, a série
 * do protocolo e o fato de a espera ter começado antes do sistema existir.
 */

export const ORIGENS = ['UBS', 'DEMANDA_REPRIMIDA'] as const;

export type OrigemSolicitacao = (typeof ORIGENS)[number];

export const ROTULOS_ORIGEM: Record<OrigemSolicitacao, string> = {
  UBS: 'Unidade de saúde',
  DEMANDA_REPRIMIDA: 'Demanda reprimida',
};

export const DESCRICOES_ORIGEM: Record<OrigemSolicitacao, string> = {
  UBS: 'Registrada pela unidade no atendimento.',
  DEMANDA_REPRIMIDA: 'Pedido anterior ao KAVI, registrado a partir do estoque da secretaria.',
};

export function ehDemandaReprimida(origem: OrigemSolicitacao): boolean {
  return origem === 'DEMANDA_REPRIMIDA';
}

/**
 * Quem registra demanda reprimida.
 *
 * Espelho do `@Perfis(REGULACAO, GESTOR)` da rota. O Administrador corrige,
 * mas não inclui: quem tem o papel em mãos é quem opera a fila.
 */
export function podeRegistrarDemandaReprimida(perfil: PerfilUsuario): boolean {
  return perfil === 'REGULACAO' || perfil === 'GESTOR';
}

/**
 * Quem registra solicitação do balcão.
 *
 * Espelho do `@Perfis(UBS, GESTOR)` de `POST /solicitacoes`. O gestor registra
 * só nas unidades que acompanha — o backend confere o escopo.
 */
export function podeRegistrarSolicitacao(perfil: PerfilUsuario): boolean {
  return perfil === 'UBS' || perfil === 'GESTOR';
}

/**
 * Se o formulário pergunta a UBS de origem.
 *
 * Na demanda reprimida sempre; no balcão, só para o gestor — a UBS registra na
 * própria unidade, que vem do vínculo.
 */
export function escolheUbsDeOrigem(origem: OrigemSolicitacao, perfil?: PerfilUsuario): boolean {
  return ehDemandaReprimida(origem) || perfil === 'GESTOR';
}

/**
 * A lista onde o registro mora — para onde o "voltar" leva.
 *
 * O detalhe e a edição têm uma URL só, seja qual for a origem. Só a listagem é
 * separada, e é ela que precisa ser escolhida ao sair de um registro.
 */
export function caminhoDaLista(origem: OrigemSolicitacao): string {
  return ehDemandaReprimida(origem) ? '/demanda-reprimida' : '/solicitacoes';
}
